import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ColorScorePipe } from 'src/app/pipe/color-score.pipe';
import { ColorScore2Pipe } from 'src/app/pipe/color-score2.pipe';
import { ColorPipe } from 'src/app/pipe/color.pipe';
import { AuthService } from 'src/app/service/auth.service';
import { RoutingService } from 'src/app/service/routing.service';
import { Entity } from 'src/app/shared/models/entity.interface';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import getScoreTypeDetails from 'src/app/shared/utils/score-type-details';
import {
  ChatChartsApiService,
  RankingScoreResult,
} from '../services/chat-charts-api.service';

interface RankingTableRow {
  name: string | undefined;
  score: number | string;
  scoreType: string;
}

@Component({
  selector: 'app-chat-charts-ranking',
  templateUrl: './chat-charts-ranking.component.html',
  styleUrls: ['./chat-charts-ranking.component.scss'],
})
export class ChatChartsRankingComponent implements OnInit, OnChanges {
  @Input() chartEntities: string[];
  @Input() childRankingScoreType: string;
  @Input() childRankingTimeframe: string;

  rankingTableData: RankingTableRow[] = [];
  entitiesRes: Entity[] = [];

  loading: boolean;
  noData: boolean;

  constructor(
    private auth: AuthService,
    private entityInfoService: EntityInfoService,
    public routing: RoutingService,
    private chatChartsApiService: ChatChartsApiService,
  ) {}

  ngOnInit(): void {
    this.getGraphRanking();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['childRankingScoreType']) {
      this.getGraphRanking();
    }
  }
  public getRankingScoreTitle(currentScoreType: string): string {
    return getScoreTypeDetails(currentScoreType).shortName;
  }

  private getGraphRanking() {
    this.loading = true;
    this.noData = false;
    this.entityInfoService
      .getEntitiesInfo(this.chartEntities ? this.chartEntities : [], 'array')
      .pipe(
        catchError((error) => {
          console.error('Error fetching entity info:', error);
          this.noData = true;
          this.loading = false;
          return of(null);
        }),
        switchMap((entities) => {
          if (this.noData || !entities) return of(null);

          const entitiesRes = (entities as Entity[]).filter(
            (element) => element !== null,
          );
          this.entitiesRes = entitiesRes;

          const content = entitiesRes.map((entity) => entity.compound_key);
          if (!this.childRankingScoreType || content.length === 0) {
            return of(null);
          }

          return this.chatChartsApiService.fetchRankingScores(
            content,
            this.childRankingScoreType,
            this.childRankingTimeframe,
          );
        }),
        catchError((error) => {
          console.error('Error fetching scores:', error);
          this.noData = true;
          this.loading = false;
          return of(null);
        }),
      )
      .subscribe((results: RankingScoreResult[] | null) => {
        if (!results || results.length === 0) {
          this.noData = true;
          this.loading = false;
          return;
        }

        this.generateRankingTableData(results);
        this.loading = false;
      });
  }

  private generateRankingTableData(results: RankingScoreResult[]) {
    const isSentiment = this.childRankingScoreType === 'sentiment';

    this.rankingTableData = results.map((entry) => {
      const entityName = this.entitiesRes?.find(
        (e) => e.compound_key === entry.entity_info.compound_key,
      )?.name;

      let score: number | string = '-';
      if (entry.lastScore !== null) {
        score = isSentiment
          ? entry.lastScore
          : Number(entry.lastScore.toFixed(2));
      }

      return {
        name: entityName,
        score,
        scoreType: this.childRankingScoreType,
      };
    });
  }

  getScoreColor(item: RankingTableRow): string {
    const scoreKey = getScoreTypeDetails(item.scoreType).scoreKey;
    if (scoreKey === 'sentiment') {
      const filterPipe = new ColorPipe(this.routing);
      return filterPipe.transform(item.score);
    } else {
      const filterPipe =
        this.auth.getScoreType(scoreKey) === 'pos'
          ? new ColorScore2Pipe(this.routing)
          : new ColorScorePipe(this.routing);
      return filterPipe.transform(item.score);
    }
  }
}
